// Signup/styles.js
// Modern, professional styles for Signup page components

import { COLORS, LAYOUT, TYPOGRAPHY, BUTTON_STYLES, CARD_STYLES } from '../../constants';

export const styles = {
  // Main container - full screen with modern layout
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: LAYOUT.containerPadding,
    background: `linear-gradient(135deg, ${COLORS.grayLight} 0%, ${COLORS.white} 100%)`,
    position: 'relative',
  },

  // Background decoration
  backgroundDecoration: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `
      radial-gradient(circle at 20% 20%, ${COLORS.primary}08 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, ${COLORS.secondary}06 0%, transparent 50%)
    `,
    pointerEvents: 'none',
  },

  // Signup card - modern card design
  signupCard: {
    ...CARD_STYLES.elevated,
    width: '100%',
    maxWidth: '520px',
    padding: '48px',
    position: 'relative',
    zIndex: 2,
    background: COLORS.white,
    borderRadius: '24px',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
    border: `1px solid ${COLORS.primary}10`,
  },

  // Header section
  header: {
    textAlign: 'center',
    marginBottom: '40px',
  },

  // ISU logo/brand section
  brandSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '24px',
  },

  brandIcon: {
    width: '48px',
    height: '48px',
    backgroundColor: COLORS.primary,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '16px',
    boxShadow: `0 4px 16px ${COLORS.shadow}`,
  },

  brandText: {
    ...TYPOGRAPHY.heading2,
    color: COLORS.primary,
    fontWeight: '700',
    fontSize: '28px',
    margin: 0,
  },

  title: {
    ...TYPOGRAPHY.heading1,
    color: COLORS.text.primary,
    fontWeight: '700',
    fontSize: '32px',
    marginBottom: '12px',
    letterSpacing: '-0.02em',
  },

  subtitle: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '16px',
    lineHeight: 1.6,
    margin: 0,
  },

  // Form section
  formSection: {
    marginBottom: '32px',
  },

  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },

  // Input groups
  inputGroup: {
    textAlign: 'left',
  },

  inputLabel: {
    display: 'block',
    ...TYPOGRAPHY.body,
    color: COLORS.text.primary,
    fontWeight: '600',
    fontSize: '14px',
    marginBottom: '8px',
  },

  inputWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },

  inputIcon: {
    position: 'absolute',
    left: '16px',
    zIndex: 2,
    color: COLORS.text.secondary,
  },

  // Name inputs container
  nameInputsContainer: {
    display: 'flex',
    gap: '16px',
    width: '100%',
  },

  nameInputGroup: {
    flex: 1,
    textAlign: 'left',
  },

  nameInput: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    fontSize: '16px',
    border: `2px solid ${COLORS.grayLight}`,
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    fontFamily: 'inherit',
  },

  input: {
    width: '100%',
    padding: '16px 16px 16px 48px',
    fontSize: '16px',
    border: `2px solid ${COLORS.grayLight}`,
    borderRadius: '12px',
    outline: 'none',
    transition: 'all 0.3s ease',
    backgroundColor: COLORS.white,
    color: COLORS.text.primary,
    fontFamily: 'inherit',
  },

  inputFocused: {
    borderColor: COLORS.primary,
    boxShadow: `0 0 0 4px ${COLORS.primary}15`,
    transform: 'translateY(-2px)',
  },

  inputError: {
    borderColor: COLORS.primary,
    boxShadow: `0 0 0 4px ${COLORS.primary}15`,
  },

  // Button styles
  submitButton: {
    ...BUTTON_STYLES.primary,
    width: '100%',
    padding: '16px 24px',
    fontSize: '16px',
    fontWeight: '600',
    borderRadius: '12px',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
  },

  submitButtonEnabled: {
    background: `linear-gradient(135deg, ${COLORS.primary} 0%, ${COLORS.primaryDark} 100%)`,
    color: COLORS.white,
    boxShadow: `0 4px 16px ${COLORS.primary}40`,
  },

  submitButtonDisabled: {
    background: COLORS.grayLight,
    color: COLORS.text.secondary,
    cursor: 'not-allowed',
    boxShadow: 'none',
  },

  // Loading spinner
  loadingSpinner: {
    width: '20px',
    height: '20px',
    border: '2px solid transparent',
    borderTop: '2px solid currentColor',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },

  // Login link section
  loginSection: {
    marginTop: '32px',
    textAlign: 'center',
    padding: '24px',
    borderRadius: '16px',
    background: `linear-gradient(135deg, ${COLORS.grayLight} 0%, ${COLORS.white} 100%)`,
    border: `1px solid ${COLORS.primary}10`,
  },

  loginText: {
    ...TYPOGRAPHY.body,
    color: COLORS.text.secondary,
    fontSize: '16px',
    marginBottom: '16px',
  },

  loginButton: {
    ...BUTTON_STYLES.outlined,
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: '600',
    border: `2px solid ${COLORS.primary}`,
    borderRadius: '12px',
    backgroundColor: 'transparent',
    color: COLORS.primary,
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    textDecoration: 'none',
  },

  // Validation message styles
  validationMessage: {
    ...TYPOGRAPHY.caption,
    color: COLORS.primary,
    fontSize: '13px',
    marginTop: '6px',
    textAlign: 'left',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },

  validationIcon: {
    color: COLORS.primary,
    fontSize: '14px',
  },

  successMessage: {
    ...TYPOGRAPHY.body,
    color: COLORS.success,
    backgroundColor: '#f0f9ff',
    border: `2px solid ${COLORS.success}30`,
    padding: '16px 20px',
    borderRadius: '12px',
    marginTop: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  successIcon: {
    color: COLORS.success,
    flexShrink: 0,
  },

  // Responsive design
  '@media (max-width: 768px)': {
    signupCard: {
      padding: '32px 24px',
      margin: '16px',
      borderRadius: '20px',
    },
    
    title: {
      fontSize: '28px',
    },
    
    brandText: {
      fontSize: '24px',
    },

    nameInputsContainer: {
      flexDirection: 'column',
      gap: '16px',
    },
  },
};